import type { AdminAsset, UpdateSettingsRequest, VersionedSettings } from '@diana/contracts';
import type { ReactNode } from 'react';
import { AutoGrowTextarea } from '../../components/AutoGrowTextarea.js';
import { Select } from '../../ui/index.js';

import { useEditorCommandRegistration } from '../../app/EditorCommandContext.js';
import { ConflictDialog } from '../../components/ConflictDialog.js';
import { SaveState } from '../../components/SaveState.js';
import { readRevisionConflict } from '../../lib/use-conflict.js';
import { useAutosave } from '../../lib/use-autosave.js';
import { ContextualMediaField } from '../media/ContextualMediaField.js';
import { websiteSections, type SettingsSection } from './settings-sections.js';

type SettingsEditorProps = {
  settings: VersionedSettings;
  assets?: readonly AdminAsset[];
  embedded?: boolean;
  section?: SettingsSection | 'all' | null;
  save(request: UpdateSettingsRequest): Promise<VersionedSettings>;
};

export function SettingsEditor({
  settings,
  assets = [],
  embedded = false,
  section = 'all',
  save,
}: SettingsEditorProps) {
  const autosave = useAutosave({
    initialValue: settings.payload,
    initialRevision: settings.revision,
    save: async ({ value, expectedRevision }) => {
      const result = await save({ expectedRevision, payload: value });
      return { value: result.payload, revision: result.revision };
    },
    readConflict: (error) =>
      readRevisionConflict(error, (current) =>
        isVersionedSettings(current) ? current.payload : null,
      ),
  });
  const payload = autosave.snapshot.value;
  useEditorCommandRegistration({
    status: autosave.snapshot.status,
    save: () => void autosave.flush(),
  });
  const update = (patch: Partial<typeof payload>) => autosave.edit({ ...payload, ...patch });
  const Wrapper = embedded ? 'section' : 'article';

  return (
    <Wrapper
      className={`${embedded ? 'settings-panel settings-panel--sectioned' : 'editor'}${section === null ? ' settings-panel--hub' : ''}`}
    >
      <div hidden={section === null && autosave.snapshot.status === 'Saved'}>
        <EditorHeading
          title={
            section === 'all'
              ? embedded
                ? 'Website'
                : 'Site settings'
              : section === 'mac'
                ? 'Mac app'
                : (websiteSections.find((item) => item.id === section)?.label ?? 'Website')
          }
          level={embedded ? 2 : 1}
          status={autosave.snapshot.status}
          onRetry={() => void autosave.retry()}
        />
      </div>

      <section className="form-section" hidden={section !== 'all' && section !== 'contacts'}>
        <div className="form-section__heading" hidden={section !== 'all'}>
          <p className="eyebrow">Identity</p>
          <h2>Global details</h2>
        </div>
        <div className="form-grid form-grid--two">
          <TextField
            label="Site name"
            value={payload.siteName}
            onChange={(siteName) => update({ siteName })}
          />
          <TextField
            label="Canonical origin"
            value={payload.canonicalOrigin ?? 'https://perova.photo'}
            onChange={(canonicalOrigin) => update({ canonicalOrigin })}
          />
          <TextField
            label="Photographer name"
            value={payload.photographerName ?? ''}
            optional
            onChange={(photographerName) =>
              update({ photographerName: photographerName || undefined })
            }
          />
          <TextField
            label="Contact email"
            type="email"
            value={payload.contactEmail}
            onChange={(contactEmail) => update({ contactEmail })}
          />
          <TextField
            label="Phone"
            value={payload.phone ?? ''}
            optional
            onChange={(phone) => update({ phone: phone || undefined })}
          />
          <TextField
            label="Location"
            value={payload.locationLabel ?? ''}
            optional
            onChange={(locationLabel) => update({ locationLabel: locationLabel || undefined })}
          />
          <TextField
            label="Service area"
            value={payload.serviceArea ?? ''}
            optional
            onChange={(serviceArea) => update({ serviceArea: serviceArea || undefined })}
          />
        </div>
      </section>

      <section className="form-section" hidden={section !== 'all' && section !== 'images'}>
        <div className="form-section__heading" hidden={section !== 'all'}>
          <p className="eyebrow">Search and sharing</p>
          <h2>Default site assets</h2>
        </div>
        <ContextualMediaField
          title="Default social image"
          profile="wide"
          cropProfile="social"
          description="Fallback when a page has no dedicated social sharing image."
          value={payload.defaultSocialImage}
          assets={assets}
          onChange={(defaultSocialImage) => update({ defaultSocialImage })}
        />
        <ContextualMediaField
          title="Logo"
          profile="compact"
          cropProfile="landscape-3-2"
          description="Brand image used by metadata and structured data."
          value={payload.logo}
          assets={assets}
          onChange={(logo) => update({ logo })}
        />
        <ContextualMediaField
          title="Favicon"
          profile="compact"
          cropProfile="square"
          description="Small site icon used by browsers."
          value={payload.favicon}
          assets={assets}
          onChange={(favicon) => update({ favicon })}
        />
      </section>

      <section className="form-section" hidden={section !== 'all' && section !== 'cta'}>
        <div className="form-section__heading" hidden={section !== 'all'}>
          <p className="eyebrow">Shared action</p>
          <h2>Global call to action</h2>
        </div>
        <div className="form-grid form-grid--three">
          <TextField
            label="CTA label"
            value={payload.globalCta.label}
            onChange={(label) => update({ globalCta: { ...payload.globalCta, label } })}
          />
          <TextField
            label="CTA destination"
            value={payload.globalCta.href}
            onChange={(href) =>
              update({
                globalCta: { ...payload.globalCta, href: href as typeof payload.globalCta.href },
              })
            }
          />
          <SelectField
            label="CTA style"
            value={payload.globalCta.style}
            options={['Primary', 'Secondary']}
            onChange={(style) =>
              update({
                globalCta: {
                  ...payload.globalCta,
                  style: style as typeof payload.globalCta.style,
                },
              })
            }
          />
        </div>
      </section>

      <section className="form-section" hidden={section !== 'all' && section !== 'social'}>
        <div className="form-section__heading" hidden={section !== 'all'}>
          <p className="eyebrow">Elsewhere</p>
          <h2>Social links</h2>
        </div>
        <div className="repeater">
          {payload.socialLinks.map((link, index) => (
            <div className="repeater__row" key={index}>
              <TextField
                label={`Social ${index + 1} label`}
                value={link.label}
                onChange={(label) => {
                  const socialLinks = [...payload.socialLinks];
                  socialLinks[index] = { ...link, label };
                  update({ socialLinks });
                }}
              />
              <TextField
                label={`Social ${index + 1} URL`}
                value={link.href}
                onChange={(href) => {
                  const socialLinks = [...payload.socialLinks];
                  socialLinks[index] = { ...link, href };
                  update({ socialLinks });
                }}
              />
              <TextField
                label={`Social ${index + 1} handle`}
                value={link.handle}
                onChange={(handle) => {
                  const socialLinks = [...payload.socialLinks];
                  socialLinks[index] = { ...link, handle };
                  update({ socialLinks });
                }}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="form-section" hidden={section !== 'all' && section !== 'menu'}>
        <h2 hidden={section !== 'all'}>Navigation and interface copy</h2>
        <TextAreaField
          label="Footer text"
          value={payload.footerText}
          onChange={(footerText) => update({ footerText })}
        />

        <div className="form-grid form-grid--two">
          {payload.navigation.primary.map((item, index) => (
            <TextField
              key={`primary-${item.href}`}
              label={`Primary navigation ${index + 1}`}
              value={item.label}
              onChange={(label) => {
                const primary = [...payload.navigation.primary];
                primary[index] = { ...item, label };
                update({ navigation: { ...payload.navigation, primary } });
              }}
            />
          ))}
          {payload.navigation.footer.map((item, index) => (
            <TextField
              key={`footer-${item.href}`}
              label={`Footer navigation ${index + 1}`}
              value={item.label}
              onChange={(label) => {
                const footer = [...payload.navigation.footer];
                footer[index] = { ...item, label };
                update({ navigation: { ...payload.navigation, footer } });
              }}
            />
          ))}
          {Object.entries(payload.chrome)
            .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
            .map(([key, value]) => (
              <TextField
                key={key}
                label={`Interface ${humanize(key)}`}
                value={value}
                onChange={(next) => update({ chrome: { ...payload.chrome, [key]: next } })}
              />
            ))}
        </div>
      </section>

      <section className="form-section settings-mac" hidden={section !== 'mac'}>
        <p>Set up gallery uploads in the Diana Photo Mac app.</p>
        <ol>
          <li>Open Diana Photo and choose Settings.</li>
          <li>Enter your API origin in API URL and your issued Mac API Token.</li>
          <li>Choose Save and Test Connection.</li>
        </ol>
        <p>Connection settings and the saved token are managed in the Mac app.</p>
      </section>

      <ConflictDialog
        open={autosave.snapshot.status === 'Conflict'}
        onAcceptServer={autosave.acceptServerVersion}
        onReapplyLocal={autosave.reapplyLocal}
        onCopyLocal={() =>
          void navigator.clipboard?.writeText(JSON.stringify(autosave.copyLocal(), null, 2))
        }
      />
    </Wrapper>
  );
}

export function EditorHeading({
  title,
  status,
  onRetry,
  action,
  level = 1,
}: {
  title: string;
  status?: ReturnType<typeof useAutosave<unknown>>['snapshot']['status'];
  onRetry?(): void;
  action?: ReactNode;
  level?: 1 | 2;
}) {
  const statusContent = status ? (
    <div className="editor-heading__status">
      <SaveState status={status} />
      {status === 'Error' && onRetry ? (
        <button className="button button--quiet" type="button" onClick={onRetry}>
          Retry save
        </button>
      ) : null}
    </div>
  ) : null;
  const Heading = level === 2 ? 'h2' : 'h1';
  return (
    <header className="editor-heading">
      <div>
        <Heading>{title}</Heading>
      </div>
      {action ? (
        <div className="editor-heading__actions">
          {statusContent}
          {action}
        </div>
      ) : (
        statusContent
      )}
    </header>
  );
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  optional,
  readOnly,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  type?: string;
  placeholder?: string;
  optional?: boolean;
  readOnly?: boolean;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {optional ? <small>Optional</small> : null}
      </span>
      <input
        aria-label={label}
        type={type}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </label>
  );
}

export function TextAreaField({
  label,
  value,
  optional = false,
  autoGrow = false,
  onChange,
}: {
  label: string;
  value: string;
  optional?: boolean;
  autoGrow?: boolean;
  onChange(value: string): void;
}) {
  const Input = autoGrow ? AutoGrowTextarea : 'textarea';
  return (
    <label className="field">
      <span>
        {label}
        {optional ? <small>Optional</small> : null}
      </span>
      <Input
        aria-label={label}
        rows={3}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </label>
  );
}

export function SelectField({
  label,
  value,
  options,
  optionLabels,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  optionLabels?: Readonly<Record<string, string>>;
  onChange(value: string): void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <Select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {optionLabels?.[option] ?? option}
          </option>
        ))}
      </Select>
    </label>
  );
}

function isVersionedSettings(value: unknown): value is VersionedSettings {
  return !!value && typeof value === 'object' && 'payload' in value && 'revision' in value;
}

function humanize(value: string) {
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}
